import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
    Box,
    Button,
    Container,
    Fade,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    TextField,
    Tooltip,
    Typography,
    IconButton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CancelIcon from "@mui/icons-material/Cancel";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

// Extend dayjs
dayjs.extend(utc);
dayjs.extend(timezone);

// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    borderRadius: 16,
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
    transition: "transform 0.2s ease-in-out",
    "&:hover": {
        transform: "translateY(-2px)",
    },
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
    "& .MuiOutlinedInput-root": {
        borderRadius: 12,
    },
}));

const UploadButton = styled(Button)(({ theme }) => ({
    borderRadius: 12,
    padding: theme.spacing(1.5, 3),
}));

const ActionButton = styled(Button)(({ theme }) => ({
    borderRadius: 8,
    padding: theme.spacing(1, 4),
}));

const DailyTaskForm = ({ dailyTask, onSubmit, onCancel }) => {
    const [formData, setFormData] = useState({
        id: "",
        type: "",
        subject: "",
        title: "",
        message: "",
        creationDateTime: dayjs().utc(),
        docs: null, // store base64
        docName: "", // store file name
        staffId: "",
        staffName: "",
        className: "",
        section: "",
    });

    const classSections = useSelector((state) => state.master.data.classSections);

    useEffect(() => {
        if (dailyTask) {
            setFormData({
                ...dailyTask,
                creationDateTime: dailyTask.creationDateTime
                    ? dayjs.utc(dailyTask.creationDateTime)
                    : dayjs().utc(),
            });
        }
    }, [dailyTask]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Check file size (2MB = 2 * 1024 * 1024 bytes)
            const maxSize = 2 * 1024 * 1024;
            if (file.size > maxSize) {
                alert(`File size is too large!\n\nMaximum allowed: 2 MB\nYour file size: ${(file.size / (1024 * 1024)).toFixed(2)} MB\n\nPlease choose a smaller file.`);
                e.target.value = null; // Reset file input
                return;
            }

            const reader = new FileReader();
            reader.onloadend = () => {
                const base64String = reader.result.split(",")[1];
                setFormData((prev) => ({
                    ...prev,
                    docs: base64String,
                    docName: file.name,
                }));
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveFile = () => {
        setFormData((prev) => ({ ...prev, docs: null, docName: "" }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const requiredFields = ["type", "subject", "title", "className", "section"];
        const missingFields = requiredFields.filter((f) => !formData[f]);

        if (missingFields.length > 0) {
            alert(`Missing required fields: ${missingFields.join(", ")}`);
            return;
        }

        const messageWordCount = formData.message.trim().split(/\s+/).length;
        if (messageWordCount < 2) {
            alert("Message must have at least 2 words.");
            return;
        }

        const submissionData = {
            ...formData,
            creationDateTime: formData.creationDateTime.toISOString(), // ✅ ISO format for JPA
        };

        if (onSubmit) onSubmit(submissionData);
    };

    return (
        <Container maxWidth="md">
            <Fade in timeout={500}>
                <StyledPaper>
                    <Typography
                        variant="h5"
                        gutterBottom
                        sx={{ mb: 4, fontWeight: 600, color: "primary.main" }}
                    >
                        {formData.id ? "Update Daily Task" : "Create New Daily Task"}
                    </Typography>

                    <form onSubmit={handleSubmit}>
                        <Grid container spacing={3}>
                            {/* Type */}
                            <Grid item xs={12} sm={6}>
                                <StyledFormControl fullWidth>
                                    <InputLabel id="type-label">Type Of Task</InputLabel>
                                    <Select
                                        labelId="type-label"
                                        name="type"
                                        value={formData.type}
                                        onChange={handleChange}
                                    >
                                        <MenuItem value="Assignment">Assignment</MenuItem>
                                        <MenuItem value="Daily Activity">Daily Activity</MenuItem>
                                    </Select>
                                </StyledFormControl>
                            </Grid>

                            {/* Subject */}
                            <Grid item xs={12} sm={6}>
                                <StyledFormControl fullWidth>
                                    <InputLabel id="subject-label">Subject</InputLabel>
                                    <Select
                                        labelId="subject-label"
                                        name="subject"
                                        value={formData.subject}
                                        onChange={handleChange}
                                    >
                                        {[
                                            "Mathematics",
                                            "English",
                                            "Science",
                                            "Social Studies",
                                            "Hindi",
                                            "Physics",
                                            "Chemistry",
                                            "Biology",
                                            "Computer Science",
                                            "Physical Education",
                                            "Art & Craft",
                                            "Music",
                                            "Geography",
                                            "History",
                                            "Economics",
                                            "Political Science",
                                            "Philosophy",
                                            "Psychology",
                                            "Other",
                                        ].map((sub) => (
                                            <MenuItem key={sub} value={sub}>
                                                {sub}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </StyledFormControl>
                            </Grid>

                            {/* Title */}
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Title"
                                    name="title"
                                    value={formData.title}
                                    onChange={handleChange}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>

                            {/* File Upload */}
                            <Grid item xs={12}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    <UploadButton
                                        variant="contained"
                                        component="label"
                                        startIcon={<CloudUploadIcon />}
                                    >
                                        Upload Document
                                        <input
                                            type="file"
                                            hidden
                                            accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,image/*"
                                            onChange={handleFileChange}
                                        />
                                    </UploadButton>

                                    {formData.docs && (
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1,
                                                bgcolor: "rgba(0,0,0,0.04)",
                                                px: 2,
                                                py: 1,
                                                borderRadius: 2,
                                            }}
                                        >
                                            <Typography variant="body2" color="success.main">
                                                {formData.docName} ✓
                                            </Typography>
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={handleRemoveFile}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    )}
                                </Box>
                            </Grid>

                            {/* Class + Section */}
                            <Grid item xs={12} sm={6}>
                                <StyledFormControl fullWidth>
                                    <InputLabel>Class Name</InputLabel>
                                    <Select
                                        name="className"
                                        value={formData.className}
                                        onChange={handleChange}
                                    >
                                        {classSections?.length > 0 ? (
                                            classSections.map((cls) => (
                                                <MenuItem key={cls.id} value={cls.name}>
                                                    {cls.name}
                                                </MenuItem>
                                            ))
                                        ) : (
                                            <MenuItem disabled>No Classes Available</MenuItem>
                                        )}
                                    </Select>
                                </StyledFormControl>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <StyledFormControl fullWidth>
                                    <InputLabel>Section</InputLabel>
                                    <Select
                                        name="section"
                                        value={formData.section}
                                        onChange={handleChange}
                                        disabled={!formData.className}
                                    >
                                        {classSections
                                            ?.find((cs) => cs.name === formData.className)
                                            ?.sections?.map((sec) => (
                                                <MenuItem key={sec.id} value={sec.name}>
                                                    {sec.name}
                                                </MenuItem>
                                            )) || <MenuItem disabled>No Sections</MenuItem>}
                                    </Select>
                                </StyledFormControl>
                            </Grid>

                            {/* Message */}
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Message"
                                    name="message"
                                    value={formData.message}
                                    onChange={handleChange}
                                    multiline
                                    rows={4}
                                    placeholder="Enter detailed message..."
                                />
                            </Grid>
                        </Grid>

                        {/* Action Buttons */}
                        <Box sx={{ mt: 4, display: "flex", justifyContent: "flex-end", gap: 2 }}>
                            <Tooltip title="Cancel">
                                <ActionButton
                                    onClick={onCancel}
                                    color="error"
                                    variant="outlined"
                                    startIcon={<CancelIcon />}
                                >
                                    Cancel
                                </ActionButton>
                            </Tooltip>

                            <Tooltip title={formData.id ? "Update Task" : "Create Task"}>
                                <ActionButton
                                    type="submit"
                                    color="primary"
                                    variant="contained"
                                    startIcon={<SaveIcon />}
                                    disabled={!formData.type || !formData.subject || !formData.title}
                                >
                                    {formData.id ? "Update Task" : "Create Task"}
                                </ActionButton>
                            </Tooltip>
                        </Box>
                    </form>
                </StyledPaper>
            </Fade>
        </Container>
    );
};

export default DailyTaskForm;
